import AuthEngine from '@/plugins/msal'

interface AuthHeaders {
  headers: Record<string, string>
}

export interface RequestPostResponse<T = unknown> {
  status: number
  data: T | null
}

type RequestError = Error & {
  response?: RequestPostResponse<unknown>
}

const request = {
  async get<T>(url: string, urlSearchParams?: Record<string, any>): Promise<T | null> {
    try {
      const params = new URLSearchParams(urlSearchParams)
      const authHeader = await headers()
      const queryString = params.toString()
      const fullUrl = queryString ? `${url}?${queryString}` : url

      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: authHeader.headers,
        credentials: 'include',
      })

      if (!response.ok) {
        if (response.status === 401 && AuthEngine.isAuthenticated()) {
          AuthEngine.logout()
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Request failed:', error)
      return null
    }
  },

  async post(url: string, body?: any): Promise<any> {
    const authHeader = await headers()
    const bodyString = body ? JSON.stringify(body) : undefined

    const response = await fetch(url, {
      method: 'POST',
      headers: authHeader.headers,
      credentials: 'include',
      body: bodyString,
    })

    const responseData = await parseOptionalJsonResponse(response)

    if (!response.ok) {
      if (response.status === 401 && AuthEngine.isAuthenticated()) {
        AuthEngine.logout()
      }
      const error: any = new Error(`HTTP error! status: ${response.status}`)
      error.response = { status: response.status, data: responseData ?? {} }
      throw error
    }

    return responseData
  },

  async postWithStatus<T>(url: string, body?: unknown): Promise<RequestPostResponse<T>> {
    const authHeader = await headers()
    const response = await fetch(url, {
      method: 'POST',
      headers: authHeader.headers,
      credentials: 'include',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })

    const responseData = await parseOptionalJsonResponse(response)

    if (!response.ok) {
      if (response.status === 401 && AuthEngine.isAuthenticated()) {
        AuthEngine.logout()
      }
      const error = new Error(`HTTP error! status: ${response.status}`) as RequestError
      error.response = { status: response.status, data: responseData ?? {} }
      throw error
    }

    return {
      status: response.status,
      data: responseData as T | null,
    }
  },

  async patch(url: string, body?: any): Promise<Response> {
    const authHeader = await headers()

    const response = await fetch(url, {
      method: 'PATCH',
      headers: authHeader.headers,
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined,
    })

    if (!response.ok) {
      if (response.status === 401 && AuthEngine.isAuthenticated()) {
        AuthEngine.logout()
      }
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response
  },

  async put(url: string, body?: any): Promise<Response> {
    const authHeader = await headers()

    const response = await fetch(url, {
      method: 'PUT',
      headers: authHeader.headers,
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined,
    })

    if (!response.ok) {
      if (response.status === 401 && AuthEngine.isAuthenticated()) {
        AuthEngine.logout()
      }
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response
  },

  async delete(url: string): Promise<Response> {
    const authHeader = await headers()

    const response = await fetch(url, {
      method: 'DELETE',
      headers: authHeader.headers,
      credentials: 'include',
    })

    if (!response.ok) {
      if (response.status === 401 && AuthEngine.isAuthenticated()) {
        AuthEngine.logout()
      }
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response
  },
}

async function headers(): Promise<AuthHeaders> {
  const isAuthenticated = AuthEngine.isAuthenticated()
  let authHeader: Record<string, string> = {}

  if (isAuthenticated) {
    const alternateLogin = AuthEngine.isAlternateLogin()
    const accessToken = alternateLogin ? '' : await AuthEngine.getAccessToken()

    if (accessToken && !alternateLogin) {
      authHeader = { Authorization: `Bearer ${accessToken}` }
    }
  }

  return {
    headers: {
      ...authHeader,
      'Content-Type': 'application/json',
    },
  }
}

async function parseOptionalJsonResponse(response: Response): Promise<unknown | null> {
  if (response.status === 204 || response.status === 205) {
    return null
  }

  const responseText = await response.text()
  if (!responseText.trim()) {
    return null
  }

  try {
    return JSON.parse(responseText)
  } catch {
    return null
  }
}

export default request
